/**
 * PushChannel — the push plane (RFC §6b, NORMATIVE; review FW-M3).
 *
 * Opens SSE against the BFF and folds validated envelopes into the store via
 * ONE sink (`apply`). Drops and reconnects are safe by construction: every
 * path (SSE frame, reconnect, poll gap, tab-return) converges on the same
 * replay contract — `fromVersion = lastApplied + 1` per stream, deduped by
 * the store's idempotency law. There is NO second reconcile path.
 *
 * ── URL contract (PINNED; the api mirrors it in Phase 3) ──
 * `GET {url}?streams=<encodeURIComponent(JSON.stringify(subs))>` where
 * `subs: Array<{streamId: string, fromVersion: number}>`. A single JSON query
 * param because streamIds contain `::` and user segments may contain `@`/`:`
 * (emails) — any character-separator scheme would collide.
 *
 * ── Frame contract (PINNED — see envelope.ts parseFrame) ──
 * `data:` carries ONE envelope OR an ARRAY of envelopes; the frame `id:` is
 * the LAST envelope's version. The server MUST group same-tick appends into
 * one array frame: the browser delivers each SSE message as its own
 * MACROTASK, so N single-envelope frames are N renders — cross-frame
 * microtask coalescing is impossible (empirically confirmed in Chrome; the
 * earlier "back-to-back frames coalesce client-side" rationale was wrong).
 * All envelopes of one frame apply inside ONE batch() → one flush (§3a).
 *
 * ── Last-Event-ID vs resume() ──
 * The server does NOT honor the browser's native `Last-Event-ID` header — BY
 * DESIGN (RFC §6b amendment 2026-07-02): a single per-connection header
 * cannot carry per-stream cursors once streams are multiplexed via
 * `?streams=`. `resume()` with explicit per-stream cursors subsumes native
 * reconnect entirely: on `error` this channel CLOSES the EventSource and
 * reopens via `resume()`, recomputing `fromVersion` from `lastApplied`.
 * Frames still set `id:` (last envelope's version) for wire debuggability
 * only. Double-delivery is dropped by the idempotency law.
 *
 * ── resume() triggers ──
 * initial `subscribe()`; EventSource `error` (with capped exponential
 * backoff: 1s · 2^attempt, CAP 30s, attempt resets after a successful
 * head-read); `document.visibilitychange` → visible (feature-detected; no-op
 * outside DOM); poll-detected gap.
 *
 * ── Poll backstop (LOAD-BEARING — Traefik drops SSE silently) ──
 * While subscribed, a low-frequency poll (default 30s) reads stream heads via
 * the injected reader; any head beyond `lastApplied` triggers `resume()`.
 *
 * ── Frame hygiene ──
 * Each frame: `JSON.parse` → `parseFrame` (never trust the wire; EVERY array
 * element validated) → ALL envelopes applied inside ONE `batch()`. Any
 * invalid element drops the WHOLE frame (logged) — the channel stays up and
 * the poll → resume path heals the gap.
 *
 * `subscribe(streamIds)` REPLACES the subscription set (call once per store
 * wiring; re-call to change the set). All I/O is injected: EventSource
 * factory, head reader, apply sink, lastApplied reader, timers, document —
 * DOM-free testable.
 */

import { batch } from "../core/signal";
import { parseFrame, type DomainEventEnvelope } from "./envelope";

/** The subset of EventSource the channel needs (injectable for tests). */
export interface EventSourceLike {
  onmessage: ((ev: { data: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  close(): void;
}

export type EventSourceFactory = (url: string) => EventSourceLike;

/** Head reader: current `{streamId, version}` per subscribed stream (poll + resume). */
export type HeadReader = (
  streamIds: readonly string[]
) => Promise<ReadonlyArray<{ streamId: string; version: number }>>;

/** Injectable timer seam (tests drive time manually). */
export interface TimerSeam {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(id: unknown): void;
}

/** The subset of `document` used for tab-return resume (injectable). */
export interface DocumentLike {
  visibilityState: string;
  addEventListener(type: string, fn: () => void): void;
  removeEventListener(type: string, fn: () => void): void;
}

export interface PushChannelOptions {
  /** Base SSE endpoint, e.g. "/api/push". */
  url: string;
  eventSource: EventSourceFactory;
  readHeads: HeadReader;
  /** The convergence sink — typically `store.apply`. */
  apply: (envelope: DomainEventEnvelope) => void;
  /** The idempotency cursor — typically `store.head`. */
  lastApplied: (streamId: string) => number | undefined;
  /** Poll backstop interval. Default 30_000 ms. */
  pollIntervalMs?: number;
  /** Timer seam. Defaults to the global timers. */
  timers?: TimerSeam;
  /** Document seam. Defaults to `globalThis.document` when present. */
  documentRef?: DocumentLike | null;
  /** Invalid-frame reporter. Defaults to console.error. */
  onFrameError?: (error: unknown, rawData: string) => void;
}

export interface PushChannel {
  /** Replace the subscription set and (re)open the stream. */
  subscribe(streamIds: readonly string[]): void;
  /** Head-read, then (re)open SSE with fromVersion = lastApplied + 1 per stream. */
  resume(): Promise<void>;
  /** Tear down SSE + poll + listeners. Idempotent. */
  close(): void;
}

const BACKOFF_BASE_MS = 1_000;
const BACKOFF_CAP_MS = 30_000;
const DEFAULT_POLL_MS = 30_000;

const defaultTimers: TimerSeam = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id as Parameters<typeof clearTimeout>[0]),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (id) =>
    clearInterval(id as Parameters<typeof clearInterval>[0]),
};

export function createPushChannel(options: PushChannelOptions): PushChannel {
  const {
    url,
    eventSource,
    readHeads,
    apply,
    lastApplied,
    pollIntervalMs = DEFAULT_POLL_MS,
    timers = defaultTimers,
    documentRef = (globalThis as { document?: DocumentLike }).document ?? null,
    onFrameError = (error, rawData) =>
      console.error("[web-kit] PushChannel dropped an invalid frame:", error, rawData),
  } = options;

  let streamIds: readonly string[] = [];
  let source: EventSourceLike | null = null;
  let closed = false;
  let attempt = 0; // backoff exponent
  let retryTimer: unknown = null;
  let pollTimer: unknown = null;
  let onVisible: (() => void) | null = null;

  const buildUrl = (): string => {
    const subs = streamIds.map((streamId) => ({
      streamId,
      fromVersion: (lastApplied(streamId) ?? 0) + 1,
    }));
    return `${url}?streams=${encodeURIComponent(JSON.stringify(subs))}`;
  };

  const closeSource = (): void => {
    if (source) {
      source.onmessage = null;
      source.onerror = null;
      source.close();
      source = null;
    }
  };

  const scheduleRetry = (): void => {
    if (closed || retryTimer !== null) return;
    const delay = Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_CAP_MS);
    attempt++;
    retryTimer = timers.setTimeout(() => {
      retryTimer = null;
      void resume();
    }, delay);
  };

  const onMessage = (ev: { data: string }): void => {
    let envelopes: DomainEventEnvelope[];
    try {
      envelopes = parseFrame(JSON.parse(ev.data));
    } catch (error) {
      onFrameError(error, ev.data); // any invalid element drops the WHOLE frame
      return;
    }
    // ONE batch per frame: N envelopes -> one flush (RFC 3a).
    batch(() => {
      for (const envelope of envelopes) apply(envelope);
    });
  };

  const openSource = (): void => {
    closeSource();
    source = eventSource(buildUrl());
    source.onmessage = onMessage;
    source.onerror = () => {
      closeSource();
      scheduleRetry();
    };
  };

  const resume = async (): Promise<void> => {
    if (closed || streamIds.length === 0) return;
    try {
      await readHeads(streamIds); // reachability probe; SSE replay closes any gap
    } catch {
      scheduleRetry(); // call plane unreachable — retry with backoff
      return;
    }
    if (closed) return; // closed while awaiting
    attempt = 0; // successful head-read resets the backoff
    openSource();
  };

  const poll = async (): Promise<void> => {
    if (closed || streamIds.length === 0) return;
    try {
      const heads = await readHeads(streamIds);
      const gap = heads.some(
        ({ streamId, version }) => version > (lastApplied(streamId) ?? 0)
      );
      if (gap && !closed) await resume(); // same path — no second reconcile
    } catch {
      // Poll failures are silent; the next tick retries. SSE errors have
      // their own backoff path.
    }
  };

  const startPoll = (): void => {
    if (pollTimer !== null) return;
    pollTimer = timers.setInterval(() => {
      void poll();
    }, pollIntervalMs);
  };

  const installVisibility = (): void => {
    if (!documentRef || onVisible) return;
    onVisible = () => {
      if (documentRef.visibilityState === "visible") void resume();
    };
    documentRef.addEventListener("visibilitychange", onVisible);
  };

  return {
    subscribe(ids: readonly string[]): void {
      if (closed) {
        throw new Error("[web-kit] PushChannel.subscribe() after close().");
      }
      streamIds = [...ids];
      startPoll();
      installVisibility();
      void resume();
    },

    resume,

    close(): void {
      if (closed) return;
      closed = true;
      closeSource();
      if (retryTimer !== null) {
        timers.clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (pollTimer !== null) {
        timers.clearInterval(pollTimer);
        pollTimer = null;
      }
      if (documentRef && onVisible) {
        documentRef.removeEventListener("visibilitychange", onVisible);
        onVisible = null;
      }
    },
  };
}
